/// <reference path="../../../../node_modules/@1js/office-online-strings/en-US/transcription-strings.d.ts" />

import * as React from 'react';
import { AppProgressBar } from '../../components';
import { CancelClicked, CleanupRequestToNetworkConnectionMonitor, GetTranscriptionFromExistingMedia, HandleInternetDisconnection} from '../../actions/LoadingActions';
import { getLoadingPageStyles, getCancelLinkStyles, getStatusLabelContainerStyle } from '../../common/pageStyles/LoadingPage.styles';
import loadingStore from '../../stores/LoadingStore';
import { Link, ILink, Label } from 'office-ui-fabric-react';
import { RouteComponentProps } from 'react-router-dom';
import { ChangeEvent } from '../../stores/utils';
import { AppLayoutSettings } from '../../common/styles/common.styles';
import { SetFailureStateAndRouteToFailure } from '../../actions/FailureActions';
import { LogEvents } from '../../../utils/LogEvents';
import { RenderToPaneKeys } from '../../common/telemetry/RenderToPaneKeys';
import { ErrorStringFromException } from '../../../utils/utils';
import { IsAuthError, IsCannotGetAuthContextError, IsNoUserSignedInError } from '../../../backend/office-js/auth/AuthErrors';
import { IForegroundTask } from '../../../backend/interfaces/IForegroundTask';
import { AppAuthenticationProviderContext } from '../../context';
import { DocumentStorageError, DocumentStorageErrors } from '../../../backend/office-js/DocumentStorageErrors';
import { FileUploadForegroundTaskError, FileUploadForegroundTaskErrors } from '../../../backend/common/fileUpload/FileUploadForegroundTaskErrors';
import { LogEventNames } from '../../../backend/interfaces/ITelemetryLogger';
import { ShouldLogErrorAsWarning } from '../../../utils/ErrorTelemetry';
import { AuthPopup } from '../../components/Popups/AuthPopup';
import { AudioBackupAndRealTimeTranscriptionConnection } from '../../common/connections/AudioBackupAndRealTimeTranscriptionConnection';
import { NetworkError, NetworkErrors } from '../../../utils/Errors';
import { TranscriptionTelemetryErrorCodes } from '../../../utils/TranscriptionTelemetryErrorCodes';
import { ITranscriptionErrorEvent } from '../../../backend/interfaces/ITranscriptionErrorEvent';
import { OneDriveUploadTaskError, OneDriveUploadTaskErrors } from '../../../backend/oneDrive/OneDriveUploadTaskErrors';
import { FILE_UPLOAD_MAX_FILE_SIZE_BYTES, RECOVERY_FLOW_MAX_FILE_SIZE_BYTES } from '../../../AppConstants';
import { TranscriptionIllustration } from '../../common/images/TranscriptionIllustration';
import { FailureContinuation } from '../../common/enums/failureEnums';
import { LoadingStatus } from '../../common/enums/loadingEnums';
import {ProgressLabelFromLoadingStatus, SetForegroundTaskInStore, StatusLabelFromLoadingStatus } from '../../common/actions/LoadingActions'
import { GetPrimaryIdentity } from '../../../backend/office-js/auth/IdentityUtil';
import { getPrimaryAccountInfoString, shouldShowOnedriveInfobar } from '../../pages/Utils';
import { PrimaryIdentityInfo } from '../../../backend/interfaces/IAuthenticationProvider';
import { getHostAppInfoBar } from '../../components/HostAppInfoBar/HostAppInfoBar';
import { HostAppSettingName, HostAppSettings } from '../../../backend/office-js/HostAppSettings';
import { TranscriptionAlert } from '../../components/Popups/TranscriptionAlert';
import { getOneDriveIdentityInfoBarLoadingPageStyles } from './Loading.styles';
import { GetNetworkConnectionMonitorInstance, InternetState, NetworkConnectionListener } from '../../NetworkConnectionMonitor';


export interface LoadingState {
  /**
   * Contains a loading page user.
   */
  foregroundTask: IForegroundTask | null;

  /**
   * Keeps track of the current kind of loading task the Loading page is performing.
   */
  loadingStatus: LoadingStatus;

  /**
   * Percent of loading done or null if we are not showing progress indicator.
   */
  percentDone: number | null;
};

function GetFailureReasonFromError(error: Error, ) : ITranscriptionErrorEvent {
  if (error instanceof FileUploadForegroundTaskError && error.name == FileUploadForegroundTaskErrors.UnsupportedFileFormat)
    return {errorEventMessage : TranscriptionStrings.UnsupportedUploadFileFormatFailureMessage, errorEventName: TranscriptionTelemetryErrorCodes.UnsupportedUploadFileFormatFailureMessage};

  if (error instanceof FileUploadForegroundTaskError && error.name == FileUploadForegroundTaskErrors.InvalidFileNameForOneDriveFileUpload)
    return {errorEventMessage : TranscriptionStrings.UnsupportedOnedriveFileNameErrorMessage, errorEventName: TranscriptionTelemetryErrorCodes.InvalidOneDriveUploadFileNameFailureReason};

  if (error instanceof FileUploadForegroundTaskError && error.name == FileUploadForegroundTaskErrors.FileExceedsSizeLimit) {
    const fileSizeUploadMaxFileSize = FILE_UPLOAD_MAX_FILE_SIZE_BYTES;
    const fileSizeUploadMaxFileSizeinMegaBytes = fileSizeUploadMaxFileSize/1000000;
    return {errorEventMessage : TranscriptionStrings.UploadTooLargeFailureReason.replace('{0}', fileSizeUploadMaxFileSizeinMegaBytes.toString()), errorEventName: TranscriptionTelemetryErrorCodes.UploadTooLargeFailureReason};
  }

  //This exception will come in case file size exceeds RecoveryFlowFileExceedsSizeLimit while we are generating transcript from recovered file.
  if (error instanceof FileUploadForegroundTaskError && error.name == FileUploadForegroundTaskErrors.RecoveryFlowFileExceedsSizeLimit) {
    const recoveryFlowUploadMaxFileSize = RECOVERY_FLOW_MAX_FILE_SIZE_BYTES
    const recoveryFlowUploadMaxFileSizeinMegaBytes = recoveryFlowUploadMaxFileSize/1000000;
    return {errorEventMessage : TranscriptionStrings.UploadTooLargeFailureReason.replace('{0}', recoveryFlowUploadMaxFileSizeinMegaBytes.toString()), errorEventName: TranscriptionTelemetryErrorCodes.RecoveryUploadTooLargeFailureReason};
  }

  if (error instanceof FileUploadForegroundTaskError && error.name == FileUploadForegroundTaskErrors.InvalidEmptyFile)
    return {errorEventMessage : TranscriptionStrings.InvalidEmptyFileFailure, errorEventName: TranscriptionTelemetryErrorCodes.InvalidEmptyFileFailure};

  if (error instanceof FileUploadForegroundTaskError && error.name == FileUploadForegroundTaskErrors.FileDecodeError)
    return {errorEventMessage : TranscriptionStrings.FileDecodeFailureReason, errorEventName: TranscriptionTelemetryErrorCodes.FileDecodeFailureReason};

  if (error instanceof OneDriveUploadTaskError && error.name == OneDriveUploadTaskErrors.OneDriveQuotaLimitReached)
    return {errorEventMessage : TranscriptionStrings.QuotaLimitReachedErrorMessage, errorEventName: TranscriptionTelemetryErrorCodes.OneDriveQuotaLimitReached};

  //ToDO- to be replaced by localized string
  if (error instanceof OneDriveUploadTaskError && error.name == OneDriveUploadTaskErrors.ForbiddenOneDriveAccess)
    return {errorEventMessage : TranscriptionStrings.ForbiddenOneDriveAccessMessage, errorEventName: TranscriptionTelemetryErrorCodes.ForbiddenOneDriveAccess};

  if (error instanceof NetworkError && error.name == NetworkErrors.NoInternetConnection) {
    return {errorEventMessage : TranscriptionStrings.ConnectionIssueFailureReason, errorEventName: TranscriptionTelemetryErrorCodes.ConnectionIssueFailureReason};
  }

  if (error instanceof DocumentStorageError && error.name == DocumentStorageErrors.WriteContentToClientStorageFailedDuringMigration) {
    return {errorEventMessage :  TranscriptionStrings.StorageMigrationFailed, errorEventName: TranscriptionTelemetryErrorCodes.WriteContentToClientStorageFailedDuringMigration};
  }

  if (IsNoUserSignedInError(error)) {
    return {errorEventMessage : TranscriptionStrings.PrivateBrowsingNotSupported, errorEventName: TranscriptionTelemetryErrorCodes.PrivateBrowsingAuthFailure};
  }

  if (IsAuthError(error)) {
    return {errorEventMessage : TranscriptionStrings.AuthenticationLoadingFailureReason, errorEventName: TranscriptionTelemetryErrorCodes.AuthenticationLoadingFailureReason};
  }

  return {errorEventMessage : TranscriptionStrings.UnknownLoadingFailureReason, errorEventName: TranscriptionTelemetryErrorCodes.UnknownLoadingFailureReason};
}

export class Loading extends React.PureComponent<RouteComponentProps<{}, {}, AudioBackupAndRealTimeTranscriptionConnection>, LoadingState> implements NetworkConnectionListener {
  declare context: React.ContextType<typeof AppAuthenticationProviderContext>;
  static contextType = AppAuthenticationProviderContext;
  private cancelLinkRef = React.createRef<ILink>();
  private _isMounted: boolean;

  private isCancelling = false;
  private isCancelSuccessful = false;
  private primaryIdentity : PrimaryIdentityInfo | null =null;

  constructor(props: RouteComponentProps<{}, {}, AudioBackupAndRealTimeTranscriptionConnection>) {
    super(props);
    this._isMounted = false;

    this.state = loadingStore.getState();
    loadingStore.on(ChangeEvent.Change, this._onStoreChanged);
  }

  public UNSAFE_componentWillMount() {
    if (HostAppSettings.IsSettingTrue(HostAppSettingName.ShouldShowOneDriveIdentityInfoDialog)) {
      this.updatePrimaryIdentity();
    }
  }

  public async componentDidMount() {
      this._isMounted = true;
      let root = document.getElementById("container");
      root?.removeAttribute("tabindex");
      GetNetworkConnectionMonitorInstance().addListener(this);
      await this._performLoadingTasks();
  }

  public componentWillUnmount() {
    this._isMounted = false;
    GetNetworkConnectionMonitorInstance().removeListener(this);
    CleanupRequestToNetworkConnectionMonitor();
  }

  onInternetConnectionStatusChanged(state: InternetState): void {
    if (!this._isMounted) {
      return;
    }
    switch(state) {
      case InternetState.INTERNET_DISCONNECTED:
        HandleInternetDisconnection(this.state.foregroundTask!, this.props.history);
        break;
    }
  }

  public render() {
    const styles = getLoadingPageStyles();
    const statusLabel = StatusLabelFromLoadingStatus(this.state.loadingStatus);
    const progressLabel = ProgressLabelFromLoadingStatus(this.state.loadingStatus);
    const percentDone = this.state.percentDone;
    const cancelButtonID = "cancelButtonTranscribe";
    return (
      <div style={AppLayoutSettings.AppCommonStyles.getPageRootContainersCSSProperties()}>
        <div>
        {getHostAppInfoBar()}
        <div style={getStatusLabelContainerStyle()}>
          <Label role="alert" aria-label={statusLabel} styles={AppLayoutSettings.AppCommonStyles.getBoldLabelStyles(AppLayoutSettings.AppFontSize.mediumFontSizePx, AppLayoutSettings.getAppColorContainer().mediumLabelColor)}>{statusLabel}</Label>
          <Label role="alert" aria-label= {percentDone != null ? Math.floor(percentDone * 100) + '%' : '' } styles={AppLayoutSettings.AppCommonStyles.getBoldLabelStyles(AppLayoutSettings.AppFontSize.mediumFontSizePx, AppLayoutSettings.getAppColorContainer().mediumLabelColor)}>{ percentDone != null ? Math.floor(percentDone * 100) + '%' : null }</Label>
        </div>
          <div className={styles.progressContainer}>
          <AppProgressBar type={'AppProgressBarProps'} description={progressLabel} ariaValueText={progressLabel} barHeight={3} percentComplete={percentDone != null ? percentDone : undefined} />
        </div>
        <div className={styles.cancelLinkContainer}>
          <Link role="button" id={cancelButtonID} componentRef={this.cancelLinkRef} styles={getCancelLinkStyles()} onClick={() => { this._onCancelClicked() }} href="#">{TranscriptionStrings.LoadingPageCancelButtonLabel}</Link>
        </div>
        <div className={styles.imageContainer}>
          <TranscriptionIllustration />
        </div>
        <AuthPopup />
        <TranscriptionAlert/>
        </div>
        { shouldShowOnedriveInfobar(this.primaryIdentity) ?
          <div style = { AppLayoutSettings.AppCommonStyles.getOneDriveIdentityInfoBarStyles(getOneDriveIdentityInfoBarLoadingPageStyles()) }>
            { getPrimaryAccountInfoString(this.primaryIdentity) }
          </div> : null }

      </div>
    );
  }

  private _onCancelClicked = async () => {
    try {
      if (this.isCancelling)
        return;

      this.isCancelling = true;
      await CancelClicked(this.props.history, this.state.foregroundTask);
      this.isCancelSuccessful = true;
      this.isCancelling = false;
      LogEvents.RenderToPane.End([{ key: RenderToPaneKeys.RenderFailureReason, value: "User canceled." }, { key: RenderToPaneKeys.RenderedSuccessfully, value: false }]);
    } catch (error: any) {
      this.isCancelling = false;
      const transcriptionErrorEvent = GetFailureReasonFromError(error);
      this._handleError(error, "Canceling threw error",  FailureContinuation.StartOver, transcriptionErrorEvent);
    }
  };

  private async _performLoadingTasks() {
    if (this.state.foregroundTask != null) {
      try {
        await this.state.foregroundTask.Execute();
      } catch (e: any) {
        SetForegroundTaskInStore(null);
        let transcriptionErrorEvent : ITranscriptionErrorEvent;
        if (IsAuthError(e)) {
          if (IsCannotGetAuthContextError(e)) {
            //ToDO- to be replaced by localized string
            transcriptionErrorEvent = {errorEventMessage : TranscriptionStrings.AnonymouseUsersErrorMessage, errorEventName: TranscriptionTelemetryErrorCodes.AuthRecoveringFailureReason};
          } else {
          transcriptionErrorEvent = {errorEventMessage : TranscriptionStrings.AuthRecoveringFailureReason, errorEventName: TranscriptionTelemetryErrorCodes.AuthRecoveringFailureReason};
          }
        } else {
          transcriptionErrorEvent = GetFailureReasonFromError(e);
        }

        this._handleError(e, "Upload and fetch threw error", FailureContinuation.Retry, transcriptionErrorEvent);
      }
    } else {
      try {
        await GetTranscriptionFromExistingMedia(this.context!, this.props.history);
      } catch (error: any) {
        let continuation:FailureContinuation = FailureContinuation.Retry;
        if (error instanceof DocumentStorageError &&
           (error.name == DocumentStorageErrors.CorruptTranscriptSource || error.name == DocumentStorageErrors.WriteContentToClientStorageFailedDuringMigration)) {
          continuation = FailureContinuation.StartOver;
        }
        const transcriptionErrorEvent = GetFailureReasonFromError(error);

        this._handleError(error, "Fetch threw error", continuation, transcriptionErrorEvent);
      }
    }
  }

  private _handleError(error: any, source: string, failureContinuation: FailureContinuation, transcriptionErrorEvent : ITranscriptionErrorEvent) {
    // If cancel is clicked and user is already redirected to landing page, ignore the one drive promise rejected due to user's cancellation action and do not show failure page
    if(this.isCancelSuccessful) {
      return;
    }
    let failureArea = LogEventNames.RenderToPane;
    let renderFailureReason = source + ". " + ErrorStringFromException(error);

    if (ShouldLogErrorAsWarning(error)) {
      failureArea = LogEventNames.Warning;
      renderFailureReason = "Render failed with warning" + ". " + ErrorStringFromException(error);
    }

    LogEvents.RenderToPane.End([{ key: RenderToPaneKeys.RenderedSuccessfully, value: false }, { key: RenderToPaneKeys.RenderFailureReason, value: renderFailureReason }]);
    SetFailureStateAndRouteToFailure(failureArea, transcriptionErrorEvent, failureContinuation, this.props.history);
  }

  private _onStoreChanged = (): void => {
    if (this._isMounted) {
      this.setState(loadingStore.getState());
    }
  };

  private async updatePrimaryIdentity() {
    this.primaryIdentity = await GetPrimaryIdentity();
    if (this._isMounted) {
      this.forceUpdate();
    }
  }
}